import type { QueryClient } from '@tanstack/react-query';

/**
 * Centralized React Query keys for the Community surface.
 *
 * Why: keys were created ad-hoc across ~12 files. Renaming or invalidating
 * was error-prone (e.g. admin saves on tabs needed manual invalidation of
 * 3+ siblings). All community consumers MUST use this object so a single
 * helper invalidates every derived query in one shot.
 *
 * Usage:
 *   useQuery({ queryKey: COMMUNITY_KEYS.posts(slug), queryFn: ... })
 *   COMMUNITY_KEYS.invalidateTabs(queryClient);
 */
export const COMMUNITY_KEYS = {
  // Tabs / tags
  tabs: () => ['community-tabs'] as const,
  tabById: (tabId: string) => ['community-tab-by-id', tabId] as const,
  tabInfo: (tabId: string) => ['community-tab-info', tabId] as const,
  tags: () => ['community-tags'] as const,

  // Posts (forum + ensino feeds)
  posts: (
    slug: string,
    page: number = 1,
    size: number = 20,
    tag: string | null = null,
  ) => ['community-posts', slug, page, size, tag] as const,
  postsBySlug: (slug: string) => ['community-posts', slug] as const,
  postsAll: () => ['community-posts'] as const,
  postDetail: (postId: string) => ['community-post-detail', postId] as const,
  postReplies: (postId: string) => ['community-post-replies', postId] as const,

  // Chat
  chatMessages: (slug: string) => ['community-chat-messages', slug] as const,
  chatMessagesAll: () => ['community-chat-messages'] as const,

  // Sidebar / counts / members
  sectionCounts: () => ['community-section-counts'] as const,
  unreadCounts: (userId: string | undefined, tabSig: string) =>
    ['community-unread-counts', userId, tabSig] as const,
  // Note: prefix is `community-` to match the historical key already used
  // by useEagerPrefetch + CommunityMembersSidebar + UserBadgeManager.
  members: () => ['community-active-members-grouped'] as const,

  // Events
  events: (tabId: string) => ['community-events', tabId] as const,
  event: (eventId: string) => ['community-event', eventId] as const,

  // Levels (re-export of the gamification single source of truth)
  levels: () => ['community-levels'] as const,
} as const;

/**
 * Helpers that invalidate logical groups in a single call so callers
 * don't have to remember every sibling key.
 */
export function invalidateCommunityTabs(qc: QueryClient) {
  qc.invalidateQueries({ queryKey: ['community-tabs'] });
  qc.invalidateQueries({ queryKey: ['community-chat-messages'] });
  qc.invalidateQueries({ queryKey: ['community-section-counts'] });
  qc.invalidateQueries({ queryKey: ['community-tabs-ensino'] });
  qc.invalidateQueries({ queryKey: ['community-tabs-desafio'] });
}

export function invalidatePostsForSlug(qc: QueryClient, slug: string) {
  qc.invalidateQueries({ queryKey: ['community-posts', slug] });
}

export function invalidatePostDetail(qc: QueryClient, postId: string) {
  qc.invalidateQueries({ queryKey: ['community-post-detail', postId] });
  qc.invalidateQueries({ queryKey: ['community-post-replies', postId] });
}